import { useState } from 'react';
import type { WebhookDelivery, WebhookDeliveryStatus } from '@salonomy/types';
import { Button, Pill, Select, Spinner } from '@salonomy/ui';
import type { PillTone } from '@salonomy/ui';
import { useRedeliverWebhook, useWebhookDeliveries } from '../../data/webhooks.js';
import { dateFormat } from '../../lib/locale.js';
import { useToast } from '../../lib/toast.js';

/**
 * Delivery log for one webhook endpoint (WS6 developer panel): each attempt's status, response code,
 * and error, newest first, filterable by status, with a redeliver action for failed/dead rows.
 * Fetched lazily — the parent only mounts this when an endpoint's log is expanded.
 */

const STATUS_TONE: Record<WebhookDeliveryStatus, PillTone> = {
  pending: 'neutral',
  delivering: 'cobalt',
  succeeded: 'success',
  failed: 'danger',
  dead: 'danger',
};

const STATUS_FILTERS: Array<{ value: '' | WebhookDeliveryStatus; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'delivering', label: 'Delivering' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
  { value: 'dead', label: 'Dead' },
];

/** Compact local datetime — "12 Jun, 14:05". Deliveries carry an absolute instant; show it plainly. */
function deliveryTime(iso: string): string {
  return dateFormat({
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

export function WebhookDeliveries({ endpointId }: { endpointId: string }) {
  const toast = useToast();
  const [status, setStatus] = useState<'' | WebhookDeliveryStatus>('');
  const { data: deliveries, isLoading } = useWebhookDeliveries(
    endpointId,
    status || undefined,
    true,
  );
  const redeliver = useRedeliverWebhook(endpointId);

  async function onRedeliver(d: WebhookDelivery) {
    try {
      await redeliver.mutateAsync(d.id);
      toast.success('Re-queued for delivery');
    } catch {
      toast.error('Could not re-queue this delivery.');
    }
  }

  return (
    <div style={{ marginTop: 10, borderTop: '1px solid var(--slm-border)', paddingTop: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="muted" style={{ fontSize: 12.5, fontWeight: 600 }}>
          Delivery log
        </div>
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value as '' | WebhookDeliveryStatus)}
          style={{ width: 170 }}
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </Select>
      </div>

      {isLoading ? (
        <div style={{ padding: '12px 0' }}>
          <Spinner />
        </div>
      ) : !deliveries || deliveries.length === 0 ? (
        <div className="muted" style={{ fontSize: 12.5, padding: '10px 0 2px' }}>
          No deliveries{status ? ` with status “${status}”` : ' yet'}.
        </div>
      ) : (
        <div className="settings-list" style={{ marginTop: 6 }}>
          {deliveries.map((d) => {
            const canRedeliver = d.status === 'failed' || d.status === 'dead';
            return (
              <div key={d.id} className="settings-row" style={{ alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <Pill tone={STATUS_TONE[d.status]} className="mono">
                      {d.status}
                    </Pill>
                    <span className="mono" style={{ fontSize: 12 }}>
                      {d.eventType}
                    </span>
                  </div>
                  <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
                    {deliveryTime(d.createdAt)} · attempt {d.attempts}/{d.maxAttempts}
                    {d.responseCode != null ? ` · HTTP ${d.responseCode}` : ''}
                  </div>
                  {d.error ? (
                    <div
                      className="muted"
                      style={{ fontSize: 11.5, marginTop: 2, color: 'var(--slm-danger)' }}
                    >
                      {d.error}
                    </div>
                  ) : null}
                </div>
                {canRedeliver ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onRedeliver(d)}
                    disabled={redeliver.isPending}
                  >
                    Redeliver
                  </Button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
